<template>
  <div class="dashboard">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 @click="handleDashboardClick" style="cursor: pointer; user-select: none;">仪表盘</h1>
      <div class="header-actions">
        <el-button type="primary" @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="YYYY-MM-DD"
          @change="handleDateChange"
        />
      </div>
    </div>

    <!-- 关键指标卡片 -->
    <el-row :gutter="20" class="key-metrics">
      <el-col :span="6">
        <el-card class="metric-card">
          <div class="metric-content">
            <div class="metric-icon" style="background-color: #1a365d;">
              <el-icon><User /></el-icon>
            </div>
            <div class="metric-info">
              <div class="metric-value">{{ formatNumber(stats.totalUsers) }}</div>
              <div class="metric-label">总用户数</div>
              <div class="metric-trend">
                <span class="trend-up" v-if="stats.userGrowth > 0">
                  <el-icon><Top /></el-icon>
                  {{ stats.userGrowth }}%
                </span>
                <span class="trend-down" v-else-if="stats.userGrowth < 0">
                  <el-icon><Bottom /></el-icon>
                  {{ Math.abs(stats.userGrowth) }}%
                </span>
                <span v-else>0%</span>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="metric-card">
          <div class="metric-content">
            <div class="metric-icon" style="background-color: #38a169;">
              <el-icon><Money /></el-icon>
            </div>
            <div class="metric-info">
              <div class="metric-value">¥{{ formatAmount(stats.totalDebt) }}</div>
              <div class="metric-label">总债务金额</div>
              <div class="metric-trend">
                <span class="trend-up" v-if="stats.debtGrowth > 0">
                  <el-icon><Top /></el-icon>
                  {{ stats.debtGrowth }}%
                </span>
                <span class="trend-down" v-else-if="stats.debtGrowth < 0">
                  <el-icon><Bottom /></el-icon>
                  {{ Math.abs(stats.debtGrowth) }}%
                </span>
                <span v-else>0%</span>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="metric-card">
          <div class="metric-content">
            <div class="metric-icon" style="background-color: #d69e2e;">
              <el-icon><Coin /></el-icon>
            </div>
            <div class="metric-info">
              <div class="metric-value">¥{{ formatAmount(stats.totalCredit) }}</div>
              <div class="metric-label">总积分余额</div>
              <div class="metric-trend">
                <span class="trend-up" v-if="stats.creditGrowth > 0">
                  <el-icon><Top /></el-icon>
                  {{ stats.creditGrowth }}%
                </span>
                <span class="trend-down" v-else-if="stats.creditGrowth < 0">
                  <el-icon><Bottom /></el-icon>
                  {{ Math.abs(stats.creditGrowth) }}%
                </span>
                <span v-else>0%</span>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="metric-card">
          <div class="metric-content">
            <div class="metric-icon" style="background-color: #e53e3e;">
              <el-icon><TrendCharts /></el-icon>
            </div>
            <div class="metric-info">
              <div class="metric-value">¥{{ formatAmount(stats.todayTransaction) }}</div>
              <div class="metric-label">今日交易额</div>
              <div class="metric-trend">
                <span class="trend-up" v-if="stats.transactionGrowth > 0">
                  <el-icon><Top /></el-icon>
                  {{ stats.transactionGrowth }}%
                </span>
                <span class="trend-down" v-else-if="stats.transactionGrowth < 0">
                  <el-icon><Bottom /></el-icon>
                  {{ Math.abs(stats.transactionGrowth) }}%
                </span>
                <span v-else>0%</span>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 图表区域 -->
    <el-row :gutter="20" class="charts-row">
      <el-col :span="12">
        <el-card class="chart-card">
          <div class="chart-header">
            <h3>用户增长趋势</h3>
            <el-select v-model="userChartType" @change="initCharts" size="small">
              <el-option label="日增长" value="daily" />
              <el-option label="周增长" value="weekly" />
              <el-option label="月增长" value="monthly" />
            </el-select>
          </div>
          <div ref="userChartRef" class="chart-container"></div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="chart-card">
          <div class="chart-header">
            <h3>债务分布</h3>
            <el-select v-model="debtChartType" @change="initCharts" size="small">
              <el-option label="金额分布" value="amount" />
              <el-option label="类型分布" value="type" />
            </el-select>
          </div>
          <div ref="debtChartRef" class="chart-container"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="charts-row">
      <el-col :span="12">
        <el-card class="chart-card">
          <div class="chart-header">
            <h3>交易统计</h3>
            <el-select v-model="transactionChartType" @change="initCharts" size="small">
              <el-option label="交易类型" value="type" />
              <el-option label="交易时间" value="time" />
            </el-select>
          </div>
          <div ref="transactionChartRef" class="chart-container"></div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="chart-card">
          <div class="chart-header">
            <h3>积分使用情况</h3>
            <el-select v-model="creditChartType" @change="initCharts" size="small">
              <el-option label="还款分" value="repayment" />
              <el-option label="现金分" value="cash" />
              <el-option label="帮还分" value="help" />
            </el-select>
          </div>
          <div ref="creditChartRef" class="chart-container"></div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 活动区域 -->
    <el-row :gutter="20" class="activity-row">
      <el-col :span="12">
        <el-card class="activity-card">
          <div class="activity-header">
            <h3>最近交易</h3>
            <el-button type="text" @click="goToTransactions">查看全部</el-button>
          </div>
          <div class="activity-list">
            <div 
              v-for="transaction in recentTransactions" 
              :key="transaction.id" 
              class="activity-item"
            >
              <div class="activity-icon" :style="{ backgroundColor: getTransactionIconBg(transaction.type) }">
                <el-icon><Trophy v-if="transaction.type === 'reward'" /></el-icon>
                <el-icon><Money v-if="transaction.type === 'transfer' || transaction.type === 'withdraw'" /></el-icon>
                <el-icon><Document v-if="transaction.type === 'other'" /></el-icon>
              </div>
              <div class="activity-content">
                <div class="activity-title">{{ transaction.title }}</div>
                <div class="activity-desc">{{ transaction.description }}</div>
                <div class="activity-time">{{ formatTime(transaction.time) }}</div>
              </div>
              <div class="activity-amount" :class="transaction.amount > 0 ? 'positive' : 'negative'">
                {{ transaction.amount > 0 ? '+' : '' }}{{ formatAmount(transaction.amount) }}
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="activity-card">
          <div class="activity-header">
            <h3>系统通知</h3>
            <el-button type="text" @click="markAllAsRead" v-if="unreadCount > 0">标记全部已读</el-button>
          </div>
          <div class="activity-list">
            <div 
              v-for="notification in notifications" 
              :key="notification.id" 
              class="notification-item"
              :class="{ unread: !notification.read }"
            >
              <div class="notification-icon" :style="{ backgroundColor: getNotificationIconBg(notification.type) }">
                <el-icon><Warning v-if="notification.type === 'warning'" /></el-icon>
                <el-icon><SuccessFilled v-if="notification.type === 'success'" /></el-icon>
                <el-icon><InfoFilled v-if="notification.type === 'info'" /></el-icon>
                <el-icon><Bell v-if="notification.type === 'notification'" /></el-icon>
              </div>
              <div class="notification-content">
                <div class="notification-title">{{ notification.title }}</div>
                <div class="notification-desc">{{ notification.description }}</div>
                <div class="notification-time">{{ formatTime(notification.time) }}</div>
              </div>
              <div class="notification-actions">
                <el-button 
                  v-if="!notification.read" 
                  type="primary" 
                  size="small" 
                  plain 
                  @click="markAsRead(notification)"
                >
                  标记已读
                </el-button>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, nextTick, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import * as echarts from 'echarts'
import { 
  Refresh, User, Money, Coin, TrendCharts, Top, Bottom,
  Trophy, Document, Warning, SuccessFilled, InfoFilled, Bell
} from '@element-plus/icons-vue'
import { dashboardApi } from "@/api/api.js"

const router = useRouter()
const route = useRoute()

const loading = ref(false)
const isMounted = ref(false)
const dateRange = ref([])

// 监听路由变化，当导航到仪表盘时重新加载数据
watch(
  () => route.path,
  (newPath, oldPath) => {
    console.log("路由变化:", oldPath, "->", newPath)
    // 如果导航到仪表盘路径，并且组件已挂载，重新加载数据
    if (newPath === "" || newPath === "/" || newPath === "/dashboard") {
      console.log("路由变化到仪表盘，重新加载数据")
      if (isMounted.value) {
        fetchDashboardData()
        nextTick(() => {
          // 重新初始化图表
          if (isMounted.value) {
            initCharts()
          }
        })
      }
    }
  },
  { immediate: true }
)

// 处理仪表盘点击事件
const handleDashboardClick = () => {
  // 强制刷新数据
  fetchDashboardData()
  nextTick(() => {
    if (isMounted.value) {
      initCharts()
    }
  })
}

// 图表类型
const userChartType = ref('daily')
const debtChartType = ref('amount')
const transactionChartType = ref('type')
const creditChartType = ref('type')

// 图表实例
const userChartRef = ref(null)
const debtChartRef = ref(null)
const transactionChartRef = ref(null)
const creditChartRef = ref(null)

let userChart = null
let debtChart = null
let transactionChart = null
let creditChart = null

// 统计数据
const stats = reactive({
  totalUsers: 0,
  userGrowth: 0,
  totalDebt: 0,
  debtGrowth: 0,
  totalCredit: 0,
  creditGrowth: 0,
  todayTransaction: 0,
  transactionGrowth: 0
})

// 最近交易
const recentTransactions = ref([
  {
    id: 1,
    type: 'transfer',
    title: '亲友帮还转账',
    description: '张三 → 李四',
    amount: 1000.00,
    time: '2026-03-05T12:00:00'
  },
  {
    id: 2,
    type: 'reward',
    title: '任务完成奖励',
    description: '王五完成任务T001',
    amount: 500.00,
    time: '2026-03-05T11:30:00'
  },
  {
    id: 3,
    type: 'withdraw',
    title: '积分提现',
    description: '赵六提现到银行卡',
    amount: -2000.00,
    time: '2026-03-05T10:00:00'
  },
  {
    id: 4,
    type: 'transfer',
    title: '积分转账',
    description: '钱七 → 孙八',
    amount: 800.00,
    time: '2026-03-05T09:00:00'
  },
  {
    id: 5,
    type: 'reward',
    title: '签到奖励',
    description: '系统发放签到积分',
    amount: 50.00,
    time: '2026-03-05T08:30:00'
  }
])

// 系统通知
const notifications = ref([
  {
    id: 1,
    type: 'warning',
    title: '系统维护通知',
    description: '系统将于今晚23:00-01:00进行维护升级',
    time: '2026-03-05T10:00:00',
    read: false
  },
  {
    id: 2,
    type: 'success',
    title: '新用户注册',
    description: '今日新增用户25人',
    time: '2026-03-05T09:30:00',
    read: true
  },
  {
    id: 3,
    type: 'info',
    title: '积分活动开始',
    description: '春季积分活动已开始，请及时参与',
    time: '2026-03-05T09:00:00',
    read: false
  },
  {
    id: 4,
    type: 'warning',
    title: '待处理交易',
    description: '有15笔待处理交易需要审核',
    time: '2026-03-05T08:30:00',
    read: false
  },
  {
    id: 5,
    type: 'info',
    title: '系统更新',
    description: '系统已更新至V2.1.0版本',
    time: '2026-03-05T08:00:00',
    read: true
  }
])

// 计算未读通知数量
const unreadCount = computed(() => {
  return notifications.value.filter(notification => !notification.read).length
})

// 格式化方法
const formatNumber = (num) => {
  return num.toLocaleString('zh-CN')
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return parseFloat(amount).toFixed(2)
}

const formatTime = (timeString) => {
  if (!timeString) return ''
  const date = new Date(timeString)
  const now = new Date()
  const diff = now - date
  
  // 小于1分钟
  if (diff < 60 * 1000) {
    return '刚刚'
  }
  
  // 小于1小时
  if (diff < 60 * 60 * 1000) {
    const minutes = Math.floor(diff / (60 * 1000))
    return `${minutes}分钟前`
  }
  
  // 小于1天
  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diff / (60 * 60 * 1000))
    return `${hours}小时前`
  }
  
  // 小于1周
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    const days = Math.floor(diff / (24 * 60 * 60 * 1000))
    return `${days}天前`
  }
  
  // 返回具体日期
  return date.toLocaleDateString('zh-CN')
}

// 获取交易图标背景色
const getTransactionIconBg = (type) => {
  switch(type) {
    case 'reward': return '#67c23a'
    case 'transfer': return '#409eff'
    case 'withdraw': return '#e6a23c'
    default: return '#909399'
  }
}

// 获取通知图标背景色
const getNotificationIconBg = (type) => {
  switch(type) {
    case 'success': return '#67c23a'
    case 'warning': return '#e6a23c'
    case 'info': return '#909399'
    default: return '#909399'
  }
}

// 初始化图表
const initCharts = () => {
  // 销毁旧图表
  // 安全检查：确保组件已挂载且DOM元素存在
  if (!isMounted.value) {
    console.warn("initCharts: 组件未挂载，跳过图表初始化")
    return
  }
  
  // 单独检查每个DOM元素
  const hasValidElements = [userChartRef.value, debtChartRef.value, transactionChartRef.value, creditChartRef.value].every(el => el !== null && el !== undefined)
  if (!hasValidElements) {
    console.warn("initCharts: 部分DOM元素不存在，跳过图表初始化")
    return
  }
  
  // 安全销毁旧图表
  try {
    if (userChart) userChart.dispose()
    if (debtChart) debtChart.dispose()
    if (transactionChart) transactionChart.dispose()
    if (creditChart) creditChart.dispose()
  } catch (error) {
    console.warn("销毁图表时出错:", error)
  }
  
  try {
    // 创建新图表
    userChart = echarts.init(userChartRef.value)
    debtChart = echarts.init(debtChartRef.value)
    transactionChart = echarts.init(transactionChartRef.value)
    creditChart = echarts.init(creditChartRef.value)
  } catch (error) {
    console.error("初始化图表时出错:", error)
    // 重置图表实例
    userChart = null
    debtChart = null
    transactionChart = null
    creditChart = null
    return
  }
  
  // 设置用户增长图表
  const userChartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: '{b}<br/>{a}: {c}人'
    },
    xAxis: {
      type: 'category',
      data: ['3月1日', '3月2日', '3月3日', '3月4日', '3月5日', '3月6日', '3月7日']
    },
    yAxis: {
      type: 'value',
      name: '用户数'
    },
    series: [{
      name: '新增用户',
      type: 'line',
      data: [25, 30, 28, 35, 40, 38, 45],
      smooth: true,
      lineStyle: {
        width: 3
      },
      itemStyle: {
        color: '#409eff'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [{
            offset: 0,
            color: 'rgba(64, 158, 255, 0.3)'
          }, {
            offset: 1,
            color: 'rgba(64, 158, 255, 0.1)'
          }]
        }
      }
    }],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true
    }
  }
  
  // 设置债务分布图表
  const debtChartOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c}元 ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      top: 'middle'
    },
    series: [{
      name: '债务分布',
      type: 'pie',
      radius: ['40%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 10,
        borderColor: '#fff',
        borderWidth: 2
      },
      label: {
        show: false,
        position: 'center'
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      labelLine: {
        show: false
      },
      data: [
        { value: 1200000, name: '信用卡债务', itemStyle: { color: '#409eff' } },
        { value: 850000, name: '个人贷款', itemStyle: { color: '#67c23a' } },
        { value: 500000, name: '消费分期', itemStyle: { color: '#e6a23c' } },
        { value: 300000, name: '其他债务', itemStyle: { color: '#f56c6c' } }
      ]
    }]
  }
  
  // 设置交易统计图表
  const transactionChartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    legend: {
      data: ['转账', '奖励', '提现', '充值']
    },
    xAxis: {
      type: 'category',
      data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
    },
    yAxis: {
      type: 'value',
      name: '金额(元)'
    },
    series: [
      {
        name: '转账',
        type: 'bar',
        stack: 'total',
        data: [3200, 2800, 3500, 4200, 3800, 4500, 5000],
        itemStyle: { color: '#409eff' }
      },
      {
        name: '奖励',
        type: 'bar',
        stack: 'total',
        data: [1500, 1200, 1800, 2000, 1600, 2200, 2500],
        itemStyle: { color: '#67c23a' }
      },
      {
        name: '提现',
        type: 'bar',
        stack: 'total',
        data: [800, 600, 900, 1200, 1000, 1500, 1800],
        itemStyle: { color: '#e6a23c' }
      },
      {
        name: '充值',
        type: 'bar',
        stack: 'total',
        data: [500, 400, 600, 800, 700, 900, 1200],
        itemStyle: { color: '#f56c6c' }
      }
    ],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true
    }
  }
  
  // 设置积分使用图表
  const creditChartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: ['1月', '2月', '3月', '4月', '5月', '6月', '7月']
    },
    yAxis: {
      type: 'value',
      name: '积分(分)'
    },
    series: [
      {
        name: '还款分',
        type: 'line',
        data: [12000, 15000, 18000, 22000, 25000, 28000, 32000],
        itemStyle: { color: '#409eff' },
        lineStyle: { width: 3 }
      },
      {
        name: '现金分',
        type: 'line',
        data: [8000, 10000, 12000, 15000, 18000, 20000, 22000],
        itemStyle: { color: '#67c23a' },
        lineStyle: { width: 3 }
      },
      {
        name: '帮还分',
        type: 'line',
        data: [5000, 6000, 8000, 10000, 12000, 15000, 18000],
        itemStyle: { color: '#e6a23c' },
        lineStyle: { width: 3 }
      }
    ],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true
    }
  }
  
  // 应用图表配置
  userChart.setOption(userChartOption)
  debtChart.setOption(debtChartOption)
  transactionChart.setOption(transactionChartOption)
  creditChart.setOption(creditChartOption)
  
  // 监听窗口大小变化
  window.addEventListener('resize', handleResize)
}

// 处理窗口大小变化
const handleResize = () => {
  if (userChart) userChart.resize()
  if (debtChart) debtChart.resize()
  if (transactionChart) transactionChart.resize()
  if (creditChart) creditChart.resize()
}

// 刷新数据
const refreshData = () => {
  loading.value = true
  // 模拟API请求
  setTimeout(() => {
    loading.value = false
    // 重新初始化图表
    initCharts()
  }, 500)
}

// 处理日期变化
const handleDateChange = () => {
  refreshData()
}

// 跳转到交易页面
const goToTransactions = () => {
  router.push('/transactions')
}

// 标记所有通知为已读
const markAllAsRead = () => {
  notifications.value.forEach(notification => {
    notification.read = true
  })
}

// 标记单个通知为已读
const markAsRead = (notification) => {
  notification.read = true
}

// 生命周期
// 获取仪表盘数据
const fetchDashboardData = async () => {
  loading.value = true
  try {
    const response = await dashboardApi.getDashboardStats()
    if (response.success) {
      const data = response.data
      // 更新统计数据
      stats.totalUsers = data.totalUsers || 0
      stats.userGrowth = data.userGrowth || 0
      stats.totalDebt = data.totalDebt || 0
      stats.debtGrowth = data.debtGrowth || 0
      stats.totalCredit = data.totalCredit || 0
      stats.creditGrowth = data.creditGrowth || 0
      stats.todayTransaction = data.todayTransaction || 0
      stats.transactionGrowth = data.transactionGrowth || 0
    }
  } catch (error) {
    console.error('获取仪表盘数据失败:', error)
    // 模拟数据以供展示
    stats.totalUsers = 1286
    stats.userGrowth = 12.5
    stats.totalDebt = 2845670.5
    stats.debtGrowth = -5.2
    stats.totalCredit = 1567890.25
    stats.creditGrowth = 8.7
    stats.todayTransaction = 45670.8
    stats.transactionGrowth = 3.4
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  isMounted.value = true
  fetchDashboardData()
  nextTick(() => {
    initCharts()
  })
})

onUnmounted(() => {
  isMounted.value = false
  
  // 安全销毁图表实例
  try {
    if (userChart) {
      userChart.dispose()
      userChart = null
    }
    if (debtChart) {
      debtChart.dispose()
      debtChart = null
    }
    if (transactionChart) {
      transactionChart.dispose()
      transactionChart = null
    }
    if (creditChart) {
      creditChart.dispose()
      creditChart = null
    }
  } catch (error) {
    console.warn("卸载时销毁图表出错:", error)
  }
  
  // 移除事件监听
  try {
    window.removeEventListener("resize", handleResize)
  } catch (error) {
    console.warn("移除事件监听器出错:", error)
  }
  
  // 清空引用
  userChartRef.value = null
  debtChartRef.value = null
  transactionChartRef.value = null
  creditChartRef.value = null
})
</script>

<style scoped>
.dashboard {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  color: #ffffff;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.key-metrics {
  margin-bottom: 20px;
}

.metric-card {
  height: 100%;
}

.metric-content {
  display: flex;
  align-items: center;
}

.metric-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.metric-icon .el-icon {
  font-size: 28px;
  color: #fff;
}

.metric-info {
  flex: 1;
}

.metric-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 4px;
}

.metric-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 4px;
}

.metric-trend {
  font-size: 12px;
}

.trend-up {
  color: #67c23a;
  display: flex;
  align-items: center;
}

.trend-down {
  color: #f56c6c;
  display: flex;
  align-items: center;
}

.trend-up .el-icon,
.trend-down .el-icon {
  font-size: 12px;
  margin-right: 2px;
}

.charts-row {
  margin-bottom: 20px;
}

.chart-card {
  height: 400px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chart-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.chart-container {
  width: 100%;
  height: 340px;
}

.activity-row {
  margin-bottom: 20px;
}

.activity-card {
  height: 100%;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.activity-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.activity-list {
  max-height: 300px;
  overflow-y: auto;
}

.activity-item,
.notification-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.activity-item:last-child,
.notification-item:last-child {
  border-bottom: none;
}

.activity-icon,
.notification-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.activity-icon .el-icon,
.notification-icon .el-icon {
  font-size: 18px;
  color: #666;
}

.activity-content,
.notification-content {
  flex: 1;
}

.activity-title,
.notification-title {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 2px;
}

.activity-desc,
.notification-desc {
  font-size: 12px;
  color: #909399;
  margin-bottom: 2px;
}

.activity-time,
.notification-time {
  font-size: 11px;
  color: #c0c4cc;
}

.activity-amount {
  font-size: 14px;
  font-weight: 500;
  min-width: 80px;
  text-align: right;
}

.activity-amount.positive {
  color: #67c23a;
}

.activity-amount.negative {
  color: #f56c6c;
}

.notification-actions {
  min-width: 80px;
  text-align: right;
}

.notification-item.unread {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  margin: 4px 0;
}

.notification-item.unread .notification-title {
  font-weight: bold;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .key-metrics .el-col {
    margin-bottom: 20px;
  }
  
  .charts-row .el-col,
  .activity-row .el-col {
    margin-bottom: 20px;
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .header-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .key-metrics .el-col {
    width: 100%;
  }
  
  .charts-row .el-col,
  .activity-row .el-col {
    width: 100%;
  }
  
  .chart-card {
    height: 300px;
  }
  
  .chart-container {
    height: 240px;
  }
}
</style>
